import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api, ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import type { LoginResponse, User } from '@/types/api';

export interface LoginInput {
  usuario: string;
  senha: string;
}

/**
 * Hook de autenticação.
 *
 * PROMPT 06: tokens ficam em cookie httpOnly (setado pelo back).
 * Aqui só manipulamos o user (dados públicos).
 */
export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, setUser, clear } = useAuthStore();

  const loginMutation = useMutation({
    mutationFn: async (input: LoginInput): Promise<LoginResponse> => {
      return api.post<LoginResponse>('/api/auth/login', input);
    },
    onSuccess: (data) => {
      // Cookie httpOnly foi setado pelo back (pep_vida_token, pep_vida_refresh)
      // data contém só { user }
      setUser(data.user);
      queryClient.clear();
      navigate({ to: '/pep-vida/dashboard' });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Back limpa os cookies (clearCookie httpOnly)
      try {
        await api.post('/api/auth/logout', {});
      } catch (err) {
        console.warn('Logout no back falhou, limpando local:', err);
      }
    },
    onSettled: () => {
      clear();
      queryClient.clear();
      navigate({ to: '/pep-vida/login' });
    },
  });

  return {
    user: user as User | null,
    isAuthenticated: !!user,
    login: loginMutation.mutateAsync,
    loginPending: loginMutation.isPending,
    loginError: loginMutation.error as ApiError | null,
    logout: logoutMutation.mutate,
  };
}
